import type {
  CompleteUploadRequest,
  CreateUploadRequest,
  FileId,
  RefreshUploadPartsResponse,
  ScopeKind,
  UploadSession,
  Uuid,
} from '@cyphex/types';

/**
 * A part PUT that object storage answered with an HTTP error. `status` 0 is a
 * network failure (no response at all). 403 on a presigned URL means the
 * signature expired, which is the one failure a retry with a NEW URL fixes.
 */
export class UploadHttpError extends Error {
  constructor(readonly status: number) {
    super(status === 0 ? 'Upload failed' : `Upload failed (${status})`);
    this.name = 'UploadHttpError';
  }
}

/**
 * Streams one part straight to object storage. Bytes never touch apps/api —
 * proxying a 200 MB file through the box would compete with meeting egress on
 * the same 1 Gbit link (ARCHITECTURE §6).
 */
export function uploadPart(
  url: string,
  blob: Blob,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  if (url.startsWith('mock://')) return simulate(blob, onProgress, signal);

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url, true);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(1);
        resolve(request.getResponseHeader('ETag') ?? '');
      } else reject(new UploadHttpError(request.status));
    };
    request.onerror = () => reject(new UploadHttpError(0));
    request.onabort = () => reject(new Error('Upload cancelled'));
    signal?.addEventListener('abort', () => request.abort());
    request.send(blob);
  });
}

/** Mock transport: real file, real timing, no network. */
function simulate(
  blob: Blob,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const steps = 12;
    let step = 0;
    const timer = setInterval(() => {
      step += 1;
      onProgress(step / steps);
      if (step >= steps) {
        clearInterval(timer);
        resolve(`"mock-${blob.size}"`);
      }
    }, 60);
    signal?.addEventListener('abort', () => {
      clearInterval(timer);
      reject(new Error('Upload cancelled'));
    });
  });
}

/** `2.4 MB` — human size for the attachment chip. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit] ?? 'KB'}`;
}

/* ── One file, every part ──────────────────────────────────────────────────── */

/** Where a file lands: the container whose members may then read it. */
export interface UploadScope {
  kind: ScopeKind;
  id: string;
}

/** The four calls an upload makes. `chatApi` in the app; a double in tests. */
export interface UploadApi {
  createUpload(body: CreateUploadRequest): Promise<UploadSession>;
  refreshUploadParts(sessionId: Uuid, partNumbers: number[]): Promise<RefreshUploadPartsResponse>;
  completeUpload(sessionId: Uuid, body: CompleteUploadRequest): Promise<unknown>;
  abortUpload(sessionId: Uuid): Promise<void>;
}

export interface UploadOptions {
  /** 0..1 across the WHOLE file, never per part. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
  /** Test seam for the retry backoff. */
  sleep?: (ms: number) => Promise<void>;
  /** `attachment` for a composer's files - see `CreateUploadRequest.purpose`. */
  purpose?: 'attachment';
}

export interface UploadedFile {
  fileId: FileId;
  sessionId: Uuid;
}

/** Parts in flight at once. Enough to fill a fast link; few enough for a slow one. */
export const UPLOAD_CONCURRENCY = 4;
/** Attempts per part before the whole upload gives up. */
export const UPLOAD_PART_ATTEMPTS = 4;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * UPLOADS A FILE THE WAY THE SERVER ISSUED IT: EVERY PART TO ITS OWN URL.
 *
 * Every caller used to PUT the WHOLE file to `parts[0].url` and complete with
 * part 1 alone. It worked by accident below 8 MiB (one part) and by a second
 * accident above it (object storage accepts one oversized part as the whole
 * object) - with no retry, no resume and one 1-hour signature for the entire
 * transfer, which is why anything large failed. Now:
 *
 *   - each part is its own byte range of the file, sent to its own URL,
 *     UPLOAD_CONCURRENCY at a time;
 *   - a failed part is retried with backoff, and a 403 (the presigned URL
 *     expired mid-transfer) first fetches a fresh URL for that part;
 *   - progress is bytes sent over the whole file;
 *   - any failure or cancel aborts the server session, so no half-file is left
 *     `pending` in somebody's Files.
 */
export async function runUpload(
  api: UploadApi,
  file: File,
  scope: UploadScope,
  options: UploadOptions = {},
): Promise<UploadedFile> {
  const sleep = options.sleep ?? wait;
  /* One switch for every part in flight: the caller's cancel, or the first
     part that gives up - the other workers must not go on sending a file that
     is already lost. */
  const local = new AbortController();
  const signal = local.signal;
  const forward = (): void => local.abort();
  options.signal?.addEventListener('abort', forward);
  if (options.signal?.aborted) local.abort();
  const session = await api.createUpload({
    filename: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
    scopeKind: scope.kind,
    scopeId: scope.id as Uuid,
    ...(options.purpose ? { purpose: options.purpose } : {}),
  });

  try {
    const parts = session.parts;
    if (parts.length === 0) throw new Error('No upload part issued');
    const sent = new Array<number>(parts.length).fill(0);
    const report = (): void => {
      if (!options.onProgress) return;
      const total = sent.reduce((sum, bytes) => sum + bytes, 0);
      options.onProgress(file.size === 0 ? 1 : Math.min(1, total / file.size));
    };
    const etags: Array<{ partNumber: number; etag: string }> = [];
    let next = 0;

    const worker = async (): Promise<void> => {
      while (next < parts.length) {
        if (signal.aborted) throw new Error('Upload cancelled');
        const index = next;
        next += 1;
        const part = parts[index]!;
        const blob = parts.length === 1 ? file : file.slice(part.rangeStart, part.rangeEnd);
        let url = part.url;
        for (let attempt = 1; ; attempt += 1) {
          try {
            const etag = await uploadPart(
              url,
              blob,
              (fraction) => {
                sent[index] = fraction * blob.size;
                report();
              },
              signal,
            );
            etags.push({ partNumber: part.partNumber, etag });
            break;
          } catch (error) {
            if (signal.aborted || attempt >= UPLOAD_PART_ATTEMPTS) {
              local.abort();
              throw error;
            }
            sent[index] = 0;
            report();
            if (error instanceof UploadHttpError && error.status === 403) {
              const fresh = await api.refreshUploadParts(session.id, [part.partNumber]);
              url = fresh.parts.find((item) => item.partNumber === part.partNumber)?.url ?? url;
            }
            await sleep(500 * 2 ** (attempt - 1));
          }
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, parts.length) }, () => worker()));
    etags.sort((a, b) => a.partNumber - b.partNumber);
    await api.completeUpload(session.id, { parts: etags });
    return { fileId: session.fileId, sessionId: session.id };
  } catch (error) {
    local.abort();
    void api.abortUpload(session.id).catch(() => undefined);
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', forward);
  }
}
